import React from 'react';

import {
	Build as BuildIcon,
	CheckCircle as CheckCircleIcon,
	ChevronRight as ChevronRightIcon,
	ErrorOutline as ErrorOutlineIcon,
	ExpandMore as ExpandMoreIcon,
	HourglassTop as HourglassTopIcon,
} from '@mui/icons-material';
import { Box, CircularProgress, Collapse, IconButton, Tooltip, Typography } from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';

import ChatMessageContent from './ChatMessageContent';
import { ChatHtmlResolver } from './ChatHtmlFileContent';
import { AttachmentUrlResolver } from './ChatAttachmentPreview';
import { selectLatestChatWidgets } from './ChatWidgetSnapshots';
import CopyToClipboardButton from '../Clipboard/CopyToClipboardButton';
import { getChatAttribute, mergeChatAttributes } from './ChatMessageAttributes';
import { ChatMessage } from './PromptChatAdapter';

export const PROGRESS_TYPE_ATTRIBUTE = 'ProgressType';
export const PROGRESS_STATUS_ATTRIBUTE = 'ProgressStatus';
export const PROGRESS_AGENT_ACTIVITY_ID_ATTRIBUTE = 'progress:agentActivityId';
export const PROGRESS_AGENT_NAME_ATTRIBUTE = 'progress:agentName';
export const PROGRESS_AGENT_RUN_ID_ATTRIBUTE = 'progress:agentRunId';
export const PROGRESS_TOOL_CALL_ID_ATTRIBUTE = 'progress:toolCallId';
export const PROGRESS_TOOL_NAME_ATTRIBUTE = 'progress:toolName';
export const PROGRESS_TOOL_TITLE_ATTRIBUTE = 'progress:toolTitle';
export const PROGRESS_DURATION_MS_ATTRIBUTE = 'progress:durationMs';
export const PROGRESS_STARTED_AT_ATTRIBUTE = 'progress:startedAt';
export const PROGRESS_FINISHED_AT_ATTRIBUTE = 'progress:finishedAt';
export const PROGRESS_ERROR_ATTRIBUTE = 'progress:error';
export const PROGRESS_TYPE_AGENT = 'agent';
export const PROGRESS_TYPE_TOOL = 'tool';
export const PROGRESS_STATUS_STARTED = 'started';
export const PROGRESS_STATUS_COMPLETED = 'completed';
export const PROGRESS_STATUS_FAILED = 'failed';
export const MESSAGE_STATUS_FINAL = 'final';
export const MESSAGE_STATUS_PARTIAL = 'partial';
const LEGACY_PROGRESS_TYPE_ATTRIBUTE = 'progress:type';
const LEGACY_PROGRESS_STATUS_ATTRIBUTE = 'progress:status';
const LEGACY_AGENT_NAME_ATTRIBUTE = 'sk:agentName';
const progressSummaryFade = keyframes`
	0%, 100% {
		opacity: 1;
	}
	50% {
		opacity: 0.52;
	}
`;

const normalizeChatAttributeValue = (value: string | undefined) => value?.trim().toLowerCase();

// New progress messages use PascalCase attributes. Keep reading the former keys so persisted conversations retain their tool timeline.
const getProgressType = (message: ChatMessage) =>
	normalizeChatAttributeValue(getChatAttribute(message.attributes, PROGRESS_TYPE_ATTRIBUTE, LEGACY_PROGRESS_TYPE_ATTRIBUTE));

const getProgressStatus = (message: ChatMessage) =>
	normalizeChatAttributeValue(getChatAttribute(message.attributes, PROGRESS_STATUS_ATTRIBUTE, LEGACY_PROGRESS_STATUS_ATTRIBUTE));

const getProgressAgentName = (attributes: Record<string, string> | undefined) =>
	getChatAttribute(attributes, PROGRESS_AGENT_NAME_ATTRIBUTE, LEGACY_AGENT_NAME_ATTRIBUTE);

export type ChatDisplayItem = { kind: 'message'; message: ChatMessage } | { kind: 'progressGroup'; messages: ChatMessage[]; partialMessages: ChatMessage[] };

type ProgressSummary = {
	color: string;
	failedText?: string;
	text: string;
};

export const isProgressChatMessage = (message: ChatMessage) => {
	const progressType = getProgressType(message);
	return progressType === PROGRESS_TYPE_TOOL || progressType === PROGRESS_TYPE_AGENT;
};

export const isToolProgressChatMessage = (message: ChatMessage) => getProgressType(message) === PROGRESS_TYPE_TOOL;

export const getChatMessageStatus = (message: ChatMessage) =>
	normalizeChatAttributeValue(getChatAttribute(message.attributes, 'Status')) ?? MESSAGE_STATUS_FINAL;

export const isPartialChatMessage = (message: ChatMessage) =>
	normalizeChatAttributeValue(getChatAttribute(message.attributes, 'Type')) === PROGRESS_TYPE_AGENT && getChatMessageStatus(message) === MESSAGE_STATUS_PARTIAL;

const mergeProgressMessages = (current: ChatMessage, next: ChatMessage): ChatMessage => ({
	...current,
	...next,
	text: next.text ?? current.text,
	attributes: mergeChatAttributes(current.attributes, next.attributes),
});

export const buildChatDisplayItems = (messages: ChatMessage[]): ChatDisplayItem[] => {
	const items: ChatDisplayItem[] = [];
	let activeProgressGroup: Extract<ChatDisplayItem, { kind: 'progressGroup' }> | undefined;
	let progressItemIndexByToolCallId = new Map<string, number>();

	const startProgressGroup = () => {
		const progressGroup: Extract<ChatDisplayItem, { kind: 'progressGroup' }> = {
			kind: 'progressGroup',
			messages: [],
			partialMessages: [],
		};
		activeProgressGroup = progressGroup;
		progressItemIndexByToolCallId = new Map<string, number>();
		items.push(progressGroup);
		return progressGroup;
	};

	for (const message of selectLatestChatWidgets(messages)) {
		if (!isProgressChatMessage(message)) {
			if (activeProgressGroup && isPartialChatMessage(message)) {
				activeProgressGroup.partialMessages.push(message);
				activeProgressGroup = undefined;
				continue;
			}

			activeProgressGroup = undefined;
			items.push({ kind: 'message', message });
			continue;
		}

		const progressGroup = (activeProgressGroup ?? startProgressGroup()).messages;
		const progressType = getProgressType(message) ?? PROGRESS_TYPE_TOOL;
		const progressId =
			progressType === PROGRESS_TYPE_AGENT
				? `${PROGRESS_TYPE_AGENT}:${getChatAttribute(message.attributes, PROGRESS_AGENT_RUN_ID_ATTRIBUTE) ?? message.id}`
				: `${PROGRESS_TYPE_TOOL}:${getChatAttribute(message.attributes, PROGRESS_TOOL_CALL_ID_ATTRIBUTE) ?? message.id}`;
		const existingIndex = progressItemIndexByToolCallId.get(progressId);
		if (existingIndex === undefined) {
			progressItemIndexByToolCallId.set(progressId, progressGroup.length);
			progressGroup.push(message);
			continue;
		}

		const mergedMessage = mergeProgressMessages(progressGroup[existingIndex], message);
		progressGroup.splice(existingIndex, 1);
		const progressIndexEntries = Array.from(progressItemIndexByToolCallId.entries());
		for (let entryIndex = 0; entryIndex < progressIndexEntries.length; entryIndex += 1) {
			const [itemProgressId, itemIndex] = progressIndexEntries[entryIndex];
			if (itemIndex > existingIndex) {
				progressItemIndexByToolCallId.set(itemProgressId, itemIndex - 1);
			}
		}

		progressItemIndexByToolCallId.set(progressId, progressGroup.length);
		progressGroup.push(mergedMessage);
	}

	return items;
};

export const formatProgressDuration = (durationMs: string | undefined) => {
	const parsed = Number(durationMs);
	if (!Number.isFinite(parsed) || parsed < 0) {
		return undefined;
	}

	const totalSeconds = Math.round(parsed / 1000);
	if (totalSeconds >= 60) {
		const minutes = Math.floor(totalSeconds / 60);
		const seconds = totalSeconds % 60;
		return `${minutes}m ${seconds}s`;
	}

	return `${(Math.round(parsed / 100) / 10).toFixed(1)} s`;
};

export const getProgressGroupDuration = (messages: ChatMessage[], now = Date.now()) => {
	const timestamps = (attribute: string) => messages
		.map((message) => Date.parse(getChatAttribute(message.attributes, attribute) ?? ''))
		.filter(Number.isFinite);
	const startedAtValues = timestamps(PROGRESS_STARTED_AT_ATTRIBUTE);
	const finishedAtValues = timestamps(PROGRESS_FINISHED_AT_ATTRIBUTE);
	const hasRunningMessages = messages.some((message) => {
		const status = getProgressStatus(message) ?? PROGRESS_STATUS_STARTED;
		return status !== PROGRESS_STATUS_COMPLETED && status !== PROGRESS_STATUS_FAILED;
	});

	if (startedAtValues.length > 0) {
		const start = Math.min(...startedAtValues);
		const durationEnd = hasRunningMessages ? now : finishedAtValues.length > 0 ? Math.max(...finishedAtValues) : start;
		const duration = formatProgressDuration(String(Math.max(0, durationEnd - start)));
		if (duration) return duration;
	}

	const reportedDurations = messages
		.map((message) => Number(getChatAttribute(message.attributes, PROGRESS_DURATION_MS_ATTRIBUTE)))
		.filter((duration) => Number.isFinite(duration) && duration >= 0);
	return reportedDurations.length > 0 ? formatProgressDuration(String(Math.max(...reportedDurations))) : undefined;
};

const getProgressDetails = (message: ChatMessage) => {
	const attributes = message.attributes ?? {};
	const progressType = getProgressType(message) ?? PROGRESS_TYPE_TOOL;
	const status = getProgressStatus(message) ?? PROGRESS_STATUS_STARTED;
	const agentName = getProgressAgentName(attributes) || 'Agent';
	const title =
		progressType === PROGRESS_TYPE_AGENT
			? agentName
			: getChatAttribute(attributes, PROGRESS_TOOL_TITLE_ATTRIBUTE) || getChatAttribute(attributes, PROGRESS_TOOL_NAME_ATTRIBUTE) || message.text || 'Working';
	const duration = formatProgressDuration(getChatAttribute(attributes, PROGRESS_DURATION_MS_ATTRIBUTE));
	const error = getChatAttribute(attributes, PROGRESS_ERROR_ATTRIBUTE);
	const isCompleted = status === PROGRESS_STATUS_COMPLETED;
	const isFailed = status === PROGRESS_STATUS_FAILED;

	return { agentName, duration, error, isCompleted, isFailed, progressType, status, title: title.replace(/^running\s+/i, '') };
};

const pendingToolMotion = keyframes`
	0%, 100% { opacity: 0.55; transform: rotate(0deg); }
	50% { opacity: 1; transform: rotate(180deg); }
`;

const finishedToolMotion = keyframes`
	0% { transform: scale(0.7); }
	55% { transform: scale(1.2); }
	100% { transform: scale(1); }
`;

const finishedToolHighlight = keyframes`
	0%, 30% { background-color: var(--tool-finished-highlight); }
	100% { background-color: transparent; }
`;

function ToolProgressItem({ message }: { message: ChatMessage }) {
	const { duration, error, isCompleted, isFailed, progressType, status, title } = getProgressDetails(message);
	const isPending = !isCompleted && !isFailed;
	const previouslyPending = React.useRef(isPending);
	const [justFinished, setJustFinished] = React.useState(false);
	React.useEffect(() => {
		const finished = previouslyPending.current && !isPending;
		previouslyPending.current = isPending;
		setJustFinished(finished);
		if (finished) {
			const timeout = window.setTimeout(() => setJustFinished(false), 650);
			return () => window.clearTimeout(timeout);
		}
	}, [isPending]);
	const Icon = isFailed ? ErrorOutlineIcon : isCompleted ? CheckCircleIcon : status === PROGRESS_STATUS_STARTED ? HourglassTopIcon : BuildIcon;
	const color = isFailed ? 'error.main' : isCompleted ? 'success.main' : 'text.secondary';
	const subject = progressType === PROGRESS_TYPE_AGENT ? 'Agent' : 'Tool';
	const statusText = isFailed ? `${subject} failed` : isCompleted ? `${subject} completed` : `${subject} in progress`;
	const statusDetailText = `${statusText}${duration ? ` - ${duration}` : ''}${error ? ` - ${error}` : ''}`;
	const trailingText = duration ?? (isFailed ? 'Failed' : isCompleted ? '' : 'Running');

	return (
		<Tooltip title={`${title} — ${statusDetailText}`}>
			<Box sx={theme => ({
				display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, borderRadius: 0.5,
				'--tool-finished-highlight': alpha(isFailed ? theme.palette.error.main : theme.palette.success.main, 0.16),
				...(justFinished && { animation: `${finishedToolHighlight} 650ms ease-out !important` }),
				'@media (prefers-reduced-motion: reduce)': { animation: 'none !important' },
			})}>
				<Icon aria-label={statusText} sx={{
					fontSize: 15, color, flexShrink: 0,
					animation: isPending ? `${pendingToolMotion} 1.8s ease-in-out infinite !important`
						: justFinished ? `${finishedToolMotion} 450ms ease-out !important` : 'none',
					'@media (prefers-reduced-motion: reduce)': { animation: 'none !important' },
				}} />
				<Typography variant="caption" title={title} sx={{ minWidth: 0, flex: 1, lineHeight: 1.25, fontWeight: 500 }} noWrap>
					{title}
				</Typography>
				<Typography
					variant="caption"
					color={isFailed ? 'error.main' : 'text.secondary'}
					sx={{ minWidth: 0, maxWidth: '40%', lineHeight: 1.2 }}
					title={trailingText}
					noWrap
				>
					{trailingText}
				</Typography>
			</Box>
		</Tooltip>
	);
}

const summarizeProgress = (messages: ChatMessage[]): ProgressSummary => {
	const details = messages.map(getProgressDetails);
	const failedTools = details.filter((item) => item.isFailed && item.progressType === PROGRESS_TYPE_TOOL).length;
	const failedAgents = details.filter((item) => item.isFailed && item.progressType !== PROGRESS_TYPE_TOOL).length;
	const running = details.filter((item) => !item.isCompleted && !item.isFailed).length;
	const completed = details.filter((item) => item.isCompleted).length;
	const completedTools = details.filter((item) => item.isCompleted && item.progressType === PROGRESS_TYPE_TOOL).length;
	const agentNames = Array.from(new Set(details.map((item) => item.agentName).filter(Boolean)));
	const agentPrefix = agentNames.length === 1 ? `${agentNames[0]} · ` : agentNames.length > 1 ? `${agentNames.length} agents · ` : '';
	const latest = messages[messages.length - 1];
	const latestDetails = latest ? getProgressDetails(latest) : undefined;

	let summary: ProgressSummary;
	if (running > 0) {
		if (latestDetails?.progressType === PROGRESS_TYPE_AGENT) {
			summary = { color: 'text.secondary', text: `Running ${latestDetails.agentName}` };
		} else {
			summary = { color: 'text.secondary', text: latestDetails ? `Running ${latestDetails.agentName}: ${latestDetails.title}` : 'Running agents' };
		}
	} else if (failedAgents > 0) {
		summary = { color: 'error.main', text: `${agentPrefix}${failedAgents} agent ${failedAgents === 1 ? 'step' : 'steps'} failed` };
	} else if (completedTools > 0) {
		summary = { color: 'success.main', text: `${agentPrefix}${completedTools} tool ${completedTools === 1 ? 'call' : 'calls'} completed` };
	} else {
		summary = { color: failedTools > 0 ? 'text.secondary' : 'success.main', text: `${agentPrefix}${completed} progress ${completed === 1 ? 'step' : 'steps'} completed` };
	}
	if (failedTools > 0) {
		const failedText = `${failedTools} tool ${failedTools === 1 ? 'call' : 'calls'} failed`;
		return { ...summary, failedText, text: `${summary.text} · ${failedText}` };
	}
	return summary;
};

const getAgentGroupKey = (message: ChatMessage) => {
	const attributes = message.attributes ?? {};
	return getChatAttribute(attributes, PROGRESS_AGENT_ACTIVITY_ID_ATTRIBUTE) || getProgressAgentName(attributes) || 'agent';
};

const getAgentTurnKey = (message: ChatMessage) => {
	const attributes = message.attributes ?? {};
	const agentKey = getAgentGroupKey(message);
	const agentRunId = getChatAttribute(attributes, PROGRESS_AGENT_RUN_ID_ATTRIBUTE);
	return agentRunId ? `${agentKey}:${agentRunId}` : agentKey;
};

const getAgentGroupTitle = (messages: ChatMessage[]) => {
	const attributes = messages[0]?.attributes ?? {};
	return getProgressAgentName(attributes) || 'Agent';
};

const normalizeAgentName = (name: string) => name.toLowerCase().replace(/[^a-z0-9]/g, '');

export const findInvokedAgentName = (toolTitle: string, agentNames: string[]) => {
	const normalizedToolTitle = normalizeAgentName(toolTitle);
	return agentNames.find((agentName) => normalizeAgentName(agentName) === normalizedToolTitle);
};

export const isAgentInvocationMessage = (message: ChatMessage, agentNames: string[]) => {
	const details = getProgressDetails(message);
	return details.progressType === PROGRESS_TYPE_TOOL && findInvokedAgentName(details.title, agentNames) != null;
};

const groupMessagesByAgentTurn = (messages: ChatMessage[]) => {
	const groups: ChatMessage[][] = [];
	let activeTurnKey: string | undefined;

	for (const message of messages) {
		const key = getAgentTurnKey(message);
		if (key !== activeTurnKey) {
			activeTurnKey = key;
			groups.push([message]);
			continue;
		}

		groups[groups.length - 1].push(message);
	}

	return groups;
};

function PartialAgentUpdate({ message, resolveHtml, resolveAttachmentUrl }: { message: ChatMessage; resolveHtml?: ChatHtmlResolver; resolveAttachmentUrl?: AttachmentUrlResolver }) {

	return (
		<Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 0.75 }}>
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.25 }}>
				<Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
					Agent update
				</Typography>
				<CopyToClipboardButton value={message.text} title="Copy agent update" disabled={!message.text} edge={false} iconSize={14} />
			</Box>
			<Box sx={{ color: 'text.secondary', fontSize: '0.76rem', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
				<ChatMessageContent message={message} resolveHtml={resolveHtml} resolveAttachmentUrl={resolveAttachmentUrl} />
			</Box>
		</Box>
	);
}

function AgentProgressPanel({ messages, agentNames, showSeparator }: { messages: ChatMessage[]; agentNames: string[]; showSeparator: boolean }) {
	const [expanded, setExpanded] = React.useState(false);
	const visibleMessages = messages.filter((message) => !isAgentInvocationMessage(message, agentNames));
	const details = visibleMessages.map(getProgressDetails);
	const failedToolCount = details.filter((item) => item.isFailed && item.progressType === PROGRESS_TYPE_TOOL).length;
	const failedAgentCount = details.filter((item) => item.isFailed && item.progressType !== PROGRESS_TYPE_TOOL).length;
	const runningCount = details.filter((item) => !item.isCompleted && !item.isFailed).length;
	const completedToolCount = details.filter((item) => item.isCompleted && item.progressType === PROGRESS_TYPE_TOOL).length;
	const agentDuration = [...details].reverse().find((item) => item.progressType === PROGRESS_TYPE_AGENT && item.duration)?.duration;
	const progressSummary = runningCount > 0
			? `${runningCount} in progress`
			: failedAgentCount > 0
				? `${failedAgentCount} agent steps failed`
			: completedToolCount > 0
				? `${completedToolCount} tool ${completedToolCount === 1 ? 'call' : 'calls'} completed`
				: failedToolCount > 0 ? 'No tool calls completed' : 'Agent completed';
	const failedToolText = failedToolCount > 0 ? `${failedToolCount} tool ${failedToolCount === 1 ? 'call' : 'calls'} failed` : undefined;
	const summary = [progressSummary, failedToolText, agentDuration].filter(Boolean).join(' · ');
	const agentName = getAgentGroupTitle(messages);
	const ToggleIcon = expanded ? ExpandMoreIcon : ChevronRightIcon;

	return (
		<Box sx={{ minWidth: 0, borderTop: showSeparator ? '1px solid' : 'none', borderColor: 'divider', pt: showSeparator ? 0.5 : 0,
			opacity: expanded ? 1 : 0.5, '&:focus-within': { opacity: 1 }, '@media (hover: hover)': { '&:hover': { opacity: 1 } },
			transition: theme => theme.transitions.create('opacity', { duration: theme.transitions.duration.shortest }) }}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, px: 0.5, py: 0.25 }}>
				<Tooltip title={expanded ? `Hide ${agentName} activity` : `Show ${agentName} activity`}>
					<IconButton
						size="small"
						aria-label={`${expanded ? 'Collapse' : 'Expand'} ${agentName} activity`}
						aria-expanded={expanded}
						onClick={() => setExpanded((value) => !value)}
						sx={{ p: 0.125, flexShrink: 0 }}
					>
						<ToggleIcon sx={{ fontSize: 15 }} />
					</IconButton>
				</Tooltip>
				<Typography variant="caption" title={agentName} sx={{ minWidth: 0, flex: '0 1 auto', lineHeight: 1.2, fontWeight: 700 }} noWrap>
					{agentName}
				</Typography>
				<Typography variant="caption" title={summary} color="text.secondary" sx={{ minWidth: 0, flex: '0 1 auto', ml: 'auto', lineHeight: 1.2 }} noWrap>
					{progressSummary}
					{failedToolText && <> · <Box component="span" sx={{ color: 'error.main' }}>{failedToolText}</Box></>}
					{agentDuration && ` · ${agentDuration}`}
				</Typography>
			</Box>
			<Collapse in={expanded} timeout="auto" unmountOnExit>
				<Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', minWidth: 0, gap: 0.25, px: 1, pb: 0.75, pt: 0.25 }}>
					{visibleMessages.map((message) => (
						<ToolProgressItem
							key={
								getChatAttribute(message.attributes, PROGRESS_TOOL_CALL_ID_ATTRIBUTE) ??
								getChatAttribute(message.attributes, PROGRESS_AGENT_RUN_ID_ATTRIBUTE) ??
								message.id
							}
							message={message}
						/>
					))}
				</Box>
			</Collapse>
		</Box>
	);
}

export function ToolProgressGroup({ messages, partialMessages = [], responding = false, resolveHtml, resolveAttachmentUrl }: { messages: ChatMessage[]; partialMessages?: ChatMessage[]; responding?: boolean; resolveHtml?: ChatHtmlResolver; resolveAttachmentUrl?: AttachmentUrlResolver }) {
	const [expanded, setExpanded] = React.useState(false);

	React.useEffect(() => {
		if (messages.some((message) => {
			const details = getProgressDetails(message);
			return details.isFailed && details.progressType !== PROGRESS_TYPE_TOOL;
		})) {
			setExpanded(true);
		}
	}, [messages]);

	const summary = summarizeProgress(messages);
	const progressDuration = getProgressGroupDuration(messages);
	const hasActiveActivity = messages.some((message) => {
		const { isCompleted, isFailed } = getProgressDetails(message);
		return !isCompleted && !isFailed;
	});
	const activitySummary = progressDuration ? `${summary.text} · ${progressDuration}` : summary.text;
	const summaryText = responding && !hasActiveActivity ? `Agent is responding… · ${activitySummary}` : activitySummary;
	const isInProgress = responding || hasActiveActivity;
	const ToggleIcon = expanded ? ExpandMoreIcon : ChevronRightIcon;
	const agentGroups = groupMessagesByAgentTurn(messages);
	const agentNames = Array.from(new Set(agentGroups.map(getAgentGroupTitle)));
	const [summaryPrefix, summarySuffix] = summary.failedText ? summaryText.split(summary.failedText) : [undefined, undefined];

	return (
		<Box sx={{ display: 'flex', minWidth: 0, justifyContent: 'flex-start', mb: 1, alignItems: 'flex-start' }}>
			<Box
				sx={{
					px: 1.25,
					py: 0.75,
					borderRadius: 2,
					bgcolor: expanded ? 'action.hover' : 'background.default',
					opacity: expanded ? 1 : 0.5,
					'&:focus-within': { opacity: 1 },
					'@media (hover: hover)': { '&:hover': { opacity: 1 } },
					border: '1px solid',
					borderColor: expanded ? 'primary.main' : 'divider',
					borderLeftWidth: 3,
					borderLeftColor: summary.color,
					transition: (theme) => theme.transitions.create(['background-color', 'border-color', 'opacity'], { duration: theme.transitions.duration.shortest }),
					width: '90%',
					maxWidth: '90%',
					minWidth: 0,
					boxSizing: 'border-box',
				}}
			>
				<Box className="randol-chat-progress-summary" aria-label="Agent activity summary" sx={{
					display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0,
					animation: isInProgress && !expanded ? `${progressSummaryFade} 1.25s ease-in-out infinite` : 'none',
					'@media (prefers-reduced-motion: reduce)': { animation: 'none' },
				}}>
					{responding && <CircularProgress size={12} aria-label="Agent is responding" sx={{ flexShrink: 0 }} />}
					<Tooltip title={expanded ? 'Hide agent activity' : 'Show agent activity'}>
						<IconButton size="small" onClick={() => setExpanded((value) => !value)} sx={{ p: 0.125, flexShrink: 0 }}>
							<ToggleIcon sx={{ fontSize: 16 }} />
						</IconButton>
					</Tooltip>
					<Typography variant="caption" color={summary.color} title={summaryText} sx={{ minWidth: 0, lineHeight: 1.25, fontWeight: 600 }} noWrap>
						{summary.failedText ? (
							<>
								{summaryPrefix}
								<Box component="span" sx={{ color: 'error.main' }}>
									{summary.failedText}
								</Box>
								{summarySuffix}
							</>
						) : (
							summaryText
						)}
					</Typography>
				</Box>
				<Collapse in={expanded} timeout="auto" unmountOnExit>
						<Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', minWidth: 0, gap: 0.75, mt: 1 }}>
						{agentGroups.map((agentMessages, index) => (
							<AgentProgressPanel
								key={`${getAgentTurnKey(agentMessages[0])}:${index}`}
								messages={agentMessages}
								agentNames={agentNames.filter((agentName) => normalizeAgentName(agentName) !== normalizeAgentName(getAgentGroupTitle(agentMessages)))}
								showSeparator={index > 0}
							/>
						))}
						{partialMessages.map((message) => (
							<PartialAgentUpdate key={message.id} message={message} resolveHtml={resolveHtml} resolveAttachmentUrl={resolveAttachmentUrl} />
						))}
					</Box>
				</Collapse>
			</Box>
		</Box>
	);
}
